import React, { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { DndContext, KeyboardSensor, PointerSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core'
import { Divider, FlowGrid, H2, List, ListItem, Paragraph, Separator } from '@fs/zion-ui'
import { ContentAdd } from '@fs/zion-icon'
import { useSlsStateContext } from '@fs/zion-sls'
import { useSharedBrowserStateContext } from '../../../../../shared/providers/sharedBrowserProvider/SharedBrowserContext'
import useRecords from '../../../../views/indexing/hooks/useRecords'
import DroppableHousehold from './components/DroppableHousehold'
import PersonListItem from './components/PersonListItem'
import Droppable from '../../../../views/indexing/components/ui/Droppable'
import PersonDragOverlay from './components/PersonDragOverlay'
import { useEditHouseholdDrag } from './hooks/useEditHouseholdDrag'
import { createHouseholdDragAnnouncements, getDragErrorMessage } from './utils/dragAnnouncements'
import { householdKeyboardCoordinateGetter } from './utils/dragKeyboardCoordinates'
import { dashedDropTargetCss, dashedDropTargetActiveCss, visuallyHiddenCss } from './utils/style.utils'
import { getDraggablePersonTestId } from './utils/testIds'

const sensorsActivationConstraint = {
  delay: 150,
  distance: 5,
}

const EditHouseholdContent = () => {
  const [t] = useTranslation()
  const zionSlsState = useSlsStateContext()
  const { thumbnails } = useSharedBrowserStateContext()

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: sensorsActivationConstraint }),
    useSensor(TouchSensor, { activationConstraint: sensorsActivationConstraint }),
    useSensor(KeyboardSensor, { coordinateGetter: householdKeyboardCoordinateGetter })
  )

  const { allRecords: households, floatingPersons } = useRecords({
    images: zionSlsState?.images,
    currentImageArk: zionSlsState?.currentImageArk,
    thumbnails,
  })

  const { dragInfo, dragError, movedPersonId, clearMovedPersonId, handleDragStart, handleDragEnd, handleDragCancel } =
    useEditHouseholdDrag({ households })

  const announcements = createHouseholdDragAnnouncements({ households, images: zionSlsState?.images, t })

  // Scopes the focus-restore DOM query below to this panel's own subtree. EditHouseholdPanel is
  // always mounted (zion-ui's InfoSheetPanel keeps children in the DOM, only toggling CSS
  // visibility), and the legacy DragNames feature reuses the same draggable-person-<id> testid
  // convention for the same person ids — an unscoped document-wide query could match either one.
  const panelRef = useRef(null)

  // A successful move re-parents the person to a new DroppableHousehold, so React unmounts their
  // old draggable element rather than reusing it, silently dropping keyboard focus to the document
  // body. Restoring focus to the person's new element keeps screen reader users in context so they
  // don't miss the drop announcement, and keeps keyboard focus visibly anchored to what moved.
  //
  // The focus move is delayed because moving focus immediately interrupts the still-being-spoken
  // "dropped into X" live region announcement with the newly-focused element's own name, cutting
  // it off. There's no programmatic way to detect when a screen reader has finished speaking, so
  // this is a fixed heuristic delay (not a condition to wait on) to give that announcement a head
  // start before the focus-change announcement follows it.
  useEffect(() => {
    if (!movedPersonId) {
      return undefined
    }

    let retryTimeoutId
    let hasRetried = false

    // The element may not have rendered yet (e.g. a slow re-render); give it one more short
    // chance before giving up, so a single slow render doesn't silently swallow the focus
    // restore for good.
    const focusMovedElement = () => {
      const movedElement = panelRef.current?.querySelector(`[data-testid="${getDraggablePersonTestId(movedPersonId)}"]`)
      if (movedElement) {
        movedElement.focus()
        clearMovedPersonId()
        return
      }
      if (hasRetried) {
        clearMovedPersonId()
        return
      }
      hasRetried = true
      retryTimeoutId = setTimeout(focusMovedElement, 250)
    }

    const timeoutId = setTimeout(focusMovedElement, 2000)

    return () => {
      clearTimeout(timeoutId)
      clearTimeout(retryTimeoutId)
    }
  }, [movedPersonId, clearMovedPersonId])

  const getNewRecordOverStyle = (isOver) => {
    if (isOver && dragInfo?.isDragging) {
      return dashedDropTargetActiveCss
    }
    return dashedDropTargetCss
  }

  return (
    <div ref={panelRef}>
      <DndContext
        sensors={sensors}
        accessibility={{ announcements }}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        {/* Corrects the DndContext's optimistic "dropped" announcement for screen reader users when
          the async save to SLS fails, since that announcement fires before the save resolves. */}
        <div aria-live="assertive" role="alert" css={visuallyHiddenCss}>
          {dragError && getDragErrorMessage({ person: dragError.person, t })}
        </div>

        <Paragraph size="sm">
          {t('edit-household.instructions', 'Moving a name will remove its previous relationships.')}
        </Paragraph>

        <Divider marginY="xxs" />

        <FlowGrid columnCount={1} bordersX gutters="sm">
          {households?.map((record) => (
            <DroppableHousehold key={record?.id} record={record} dragInfo={dragInfo} />
          ))}
        </FlowGrid>

        {/* Names not in a household */}
        {floatingPersons?.length > 0 && (
          <>
            <Divider marginY="xxs" />
            <H2 size="H5">{t('edit-household.no-household.heading', 'Names not in a household')}</H2>
            <Separator size="xxs" />
            <List>
              {floatingPersons.map((person) => (
                <PersonListItem key={person?.id} person={person} dragInfo={dragInfo} />
              ))}
            </List>
          </>
        )}

        {/* New Household */}
        <Divider marginY="xxs" />
        <H2 size="H5">{t('edit-household.new-household.heading', 'New Household')}</H2>
        <Separator size="xs" />
        <Droppable id="new_record" setOverStyle={getNewRecordOverStyle}>
          <List>
            <ListItem
              startElement={<ListItem.Icon Icon={ContentAdd} />}
              primaryText={t('edit-household.new-household.description', 'Drag a name here to create a new household.')}
            />
          </List>
        </Droppable>
        <Separator size="xs" />

        <Divider marginY="xxs" />

        <PersonDragOverlay dragInfo={dragInfo} />
      </DndContext>
    </div>
  )
}

export default EditHouseholdContent
